import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// MUI Components
import {
  makeStyles,
  Container,
  Button,
  Box,
  InputBase,
  Grid,
  Typography,
  Dialog,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'

// main components
import { InvoiceTimeline } from '../components/invoices/InvoiceTimeline'
import { InvoiceHeaderComponent } from '../components/invoices/InvoiceHeader'
import { InvoiceDetailv2Component } from '../components/invoices/InvoiceDetailv2'
import LoadingBox from '../components/invoices/LoadingBox'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  getInvoice,
  getWoList,
  submitInvoice,
  getLineItemTypes,
  getRates,
  putCompanyBillDisclosure
} from '../services/ApiService'
import { ScheduledDetailComponent } from '../components/invoices/ScheduledDetail'
import { McssfrScheduledDetailComponent } from '../components/invoices/McssfrScheduledDetail'
import { AdvancePaymentDetailComponent } from '../components/invoices/AdvancePaymentDetail'
import { useDispatch, useSelector } from 'react-redux'
import {
  mcssfr,
  mcs360,
  ferrandino,
  editableInvoiceStatus
} from '../lib/Constants'
import { isRunningInIframe } from '../lib/Global'
import ReactGA from 'react-ga4'
import { DetailedInfo } from '../components/workorders/DetailedInfo'
import { isEqual } from 'lodash'
import { loadingActions } from '../store/loading'
import { SubmitConfirmation } from '../components/invoices/SubmitConfirmation'
import { authActions } from '../store/signIn'
import { ConfirmDialog } from '../components/form/ConfirmDialog'

const useStyles = makeStyles(theme => ({
  scrollContainer: {
    margin: '0px',
    padding: '0px',
    maxWidth: '100%',
    overflowY: 'auto'
  },
  container: {
    backgroundColor: theme.colors.mainContainerBackground,
    paddingLeft: '0px',
    paddingRight: '0px',
    margin: '0px',
    maxWidth: '100%',
    minWidth: 'fit-content'
  },
  mainContainer: {
    display: 'flex',
    backgroundColor: 'white',
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0'
    },
    minHeight: '100vh'
  },
  leftContainer: {
    paddingRight: 0,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0'
    }
  },
  rightContainer: {
    width: '360px',
    padding: '0'
  },
  bottomContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    padding: '10px',
    boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.1)'
  },
  globalHeaderContainer: {
    height: '72px',
    backgroundColor: 'white',
    maxWidth: '100%',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  backButton: {
    fontSize: '14px',
    fontWeight: '500',
    height: '58px',
    width: '105px',
    textTransform: 'none',
    marginLeft: '68px',
    boxShadow: '6px 9px 43px 0px ' + theme.colors.invoiceColors.shadow,
    [theme.breakpoints.down('xs')]: {
      marginLeft: '12px'
    }
  },
  invoiceHeader: {
    marginTop: '20px',
    marginLeft: '20px',
    boxShadow: '6px 9px 43px 0px ' + theme.colors.invoiceColors.shadow,
    [theme.breakpoints.down('xs')]: {
      marginLeft: '12px'
    }
  },
  invoiceDetail: {
    paddingLeft: '20px',
    paddingRight: '20px'
  },
  notesContainer: {
    padding: '20px 0px 20px',
    marginBottom: '200px'
  },
  notesContainerHidden: {
    display: 'none'
  },
  miscNotes: {
    borderRadius: '8px',
    backgroundColor: theme.palette.common.white,
    border: '1px solid ' + theme.colors.invoiceColors.header_grey,
    fontSize: 12,
    fontWeight: 400,
    width: '100%',
    'label + &': {
      marginTop: theme.spacing(1)
    },
    padding: '10px 12px 16px 8px'
  },
  miscNotesMandatory: {
    borderRadius: '8px',
    backgroundColor: theme.palette.common.white,
    border: '1px solid ' + theme.colors.errorText,
    fontSize: 12,
    fontWeight: 400,
    width: '100%',
    'label + &': {
      marginTop: theme.spacing(1)
    },
    padding: '10px 12px 16px 8px'
  },
  miscLabel: {
    width: '200px',
    fontSize: 14,
    fontWeight: 500
  },
  // styles for InvoiceStatusBoxes
  firstBox: {
    color: 'white',
    background: theme.colors.invoiceColors.dark_grey,
    height: '56px',
    width: '160px',
    borderRadius: '10px 0 0 10px',
    borderLeft: '0.5px solid ' + theme.colors.invoiceColors.disabled_grey,
    textAlign: 'center',
    padding: '18px 0'
  },
  middleBox: {
    color: theme.colors.invoiceColors.disabled_grey,
    height: '56px',
    width: '160px',
    border: '0.5px solid ' + theme.colors.invoiceColors.disabled_grey,
    borderRight: 'none',
    textAlign: 'center',
    padding: '18px 0'
  },
  middleCompleteBox: {
    color: 'white',
    background: theme.colors.invoiceColors.dark_grey,
    height: '56px',
    width: '160px',
    borderLeft: '0.5px solid ' + theme.colors.invoiceColors.disabled_grey,
    textAlign: 'center',
    padding: '18px 0'
  },
  lastBox: {
    color: theme.colors.invoiceColors.disabled_grey,
    height: '56px',
    width: '160px',
    border: '0.5px solid ' + theme.colors.invoiceColors.disabled_grey,
    borderRadius: '0 10px 10px 0',
    textAlign: 'center',
    padding: '18px 0'
  },
  lastCompleteBox: {
    color: theme.colors.invoiceColors.approved_green,
    height: '56px',
    width: '160px',
    borderRadius: '0 10px 10px 0',
    border: '0.5px solid ' + theme.colors.invoiceColors.approved_green,
    textAlign: 'center',
    padding: '18px 0'
  },
  declinedBox: {
    height: '56px',
    width: '160px',
    border: '1px solid ' + theme.colors.invoiceColors.red_error,
    borderRadius: '0 10px 10px 0',
    textAlign: 'center',
    padding: '18px 0',
    color: theme.colors.invoiceColors.red_error
  },
  attachIcon: {
    width: '2px',
    color: theme.palette.primary.light
  },
  notesLabel: {
    backgroundColor: theme.colors.invoiceColors.summary_bg,
    fontSize: '12px',
    height: '22px',
    paddingTop: '3px',
    paddingBottom: '3px',
    paddingLeft: '15px',
    color: theme.colors.invoiceColors.dark_grey,
    fontWeight: '500',
    borderBottom: '0px',
    marginBottom: '15px'
  },
  notesLabelMandatory: {
    backgroundColor: theme.colors.invoiceColors.summary_bg,
    fontSize: '12px',
    height: '22px',
    paddingTop: '3px',
    paddingBottom: '3px',
    paddingLeft: '15px',
    color: theme.colors.errorText,
    fontWeight: '500',
    borderBottom: '0px',
    marginBottom: '15px'
  },
  attachChip: {
    fontSize: '12px',
    backgroundColor: theme.colors.backdropColor,
    border: `0.5px solid ${theme.palette.primary.light}`,
    color: theme.palette.primary.light,
    marginTop: '4px'
  },
  gridQuestion: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly'
  },
  question: {
    fontSize: '10px',
    fontWeight: '400',
    color: theme.colors.invoiceColors.dark_grey,
    display: 'inline-block'
  },
  chatButton: {
    fontSize: '14px',
    fontWeight: '500',
    height: '40px',
    width: '99px',
    textTransform: 'none',
    boxShadow: '6px 9px 43px 0px ' + theme.colors.invoiceColors.shadow,
    border: '0.5px solid ' + theme.colors.textGray,
    borderRadius: '8px'
  },
  chatText: {
    fontSize: '14px',
    fontWeight: '500',
    color: theme.colors.blue
  },
  chatIcon: {
    color: theme.colors.blue
  },
  flexBox_end: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  submitButton: {
    color: 'primary',
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '160px'
  },
  draftButton: {
    color: theme.colors.proposalColors.buttons.draft,
    border: '0.5px solid ' + theme.colors.proposalColors.buttons.draft,
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '160px'
  },
  dialogContainer: {
    textAlign: 'center',
    padding: '24px'
  },
  dialogActionsContainer: {
    justifyContent: 'right',
    paddingBottom: '24px'
  },
  iconDialog: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  mainMessage: {
    fontSize: '24px',
    fontWeight: 'bolder',
    marginBottom: '16px'
  },
  leaveTitle2: {
    fontSize: '18px',
    fontWeight: 'normal'
  },
  secondaryMessage: {
    fontSize: '16px',
    marginBottom: '24px'
  },
  button: {
    color: 'primary',
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '118px',
    borderRadius: '30px'
  },
  leaveButton: {
    color: theme.colors.proposalColors.buttons.draft,
    border: '0.5px solid ' + theme.colors.proposalColors.buttons.draft,
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '118px',
    borderRadius: '30px'
  }
}))

const CreateInvoice = () => {
  const params = useParams()
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const [lineItems, setLineItems] = useState()
  const [allItems, setAllItems] = useState()
  const [totalAmount, setTotalAmount] = useState(0)
  const [selectedCheck, setSelectedCheck] = useState(false)
  const location = useLocation()
  const [data, setData] = useState()
  const [itemTypes, setItemTypes] = useState()
  const [pricingEngine, setPricingEngine] = useState(false)
  const [rates, setRates] = useState(null)
  const [laborQuantity, setLaborQuantity] = useState(0)
  const [auditTrail, setAuditTrail] = useState()
  const history = useHistory()
  const [notes, setNotes] = useState('')
  const [invoiceStatus, setInvoiceStatus] = useState()
  const [loading, setLoading] = useState(false)
  const [saveSubmitButtonsInitialized, setSaveSubmitButtonsInitialized] =
    useState(false)
  const userData = useSelector(state => state.auth?.user?.userInfo)
  const isFAS = userData.parent_subdomain === ferrandino
  const [scheduledInvoice, setScheduledInvoice] = useState(false)
  const [multipleWoNumbers, setMultipleWoNumbers] = useState([])
  const [projectInvoice, setProjectInvoice] = useState(false)
  const [totalProjectAmount, setTotalProjectAmount] = useState(0)
  const [projectAmountPaid, setProjectAmountPaid] = useState(0)
  const [balanceDue, setBalanceDue] = useState(0)
  const [amountPaid, setBalDueAmountPaid] = useState(0)
  const userStore = useSelector(state => state.auth.user)
  const loadingFiles = useSelector(state => state.invoices.loadingFiles)
  const [disableSubmitButton, setDisableSubmitButton] = useState(false)
  const [disableDraftButton, setDisableDraftButton] = useState(true)
  const [lastSavedData, setLastSavedData] = useState({})
  const [attachments, setAttachments] = useState([])
  const [isNotesFilled, setIsNotesFilled] = useState(false)
  const [selectedWo, setSelectedWo] = useState(null)
  const [reload, setReload] = useState(false)
  const [pricingOverride, setPricingOverride] = useState({
    isPricingOverride: false,
    nte: null
  })
  const [nteMessageEnabled, setNteMessageEnabled] = useState(false)
  const [openBackDialog, setOpenBackDialog] = useState(false)
  const paymentInquiryChat = userStore?.userInfo?.payment_inquiry_chat
  const billDisclosureStore = useSelector(state => state.auth.billDisclosure)
  const [nteValue, setNteValue] = useState(0)
  const [dialogNotes, setDialogNotes] = useState('')
  const [submitConfirmation, setSubmitConfirmation] = useState({
    title: '',
    content: '',
    open: false
  })
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [nteExceedConfirmDialogOpen, setNteExceedConfirmDialogOpen] =
    useState(false)
  const canExceedNTEConfig = useSelector(
    state =>
      state.auth?.user?.userInfo?.configurations?.portals?.subcontractor
        ?.invoices?.can_exceed_nte
  )
  const notesLabelConfig = useSelector(
    state =>
      state.auth?.user?.userInfo?.configurations?.portals?.subcontractor
        ?.invoices?.labels?.notes
  )
  const lienMandatory =
    userStore?.userInfo?.configurations?.portals?.subcontractor?.invoices
      ?.lien_mandatory

  function calculateTotal(items) {
    let total = 0
    items.forEach(item => {
      const lineItemsTotal = item.subtotal_line_items
      const markdownAmount = lineItemsTotal * item.sub_markdown
      total += lineItemsTotal - markdownAmount
    })
    return total
  }
  useEffect(async () => {
    setLoading(true)
    const searchParams = location.search
    const invoiceId = new URLSearchParams(searchParams).get('id')
    try {
      const response = await getInvoice(invoiceId)
      const lineItemTypes = await getLineItemTypes()
      setPricingEngine(userStore?.userInfo?.pricing_engine)
      let respRates = null
      if (response?.content?.work_order?.external_id) {
        respRates = await getRates(response?.content?.work_order?.external_id)
      }
      if (response?.content?.status === 'deleted') {
        history.push({ pathname: 'invoices' })
      }
      const tempTotalProjectAmount = calculateTotal(
        response?.content?.line_items
      )
      if (response?.content?.attachments) {
        setAttachments(response?.content?.attachments)
      }
      const advancePaymentValue = response?.content?.work_order?.advance_payment
      setTotalProjectAmount(tempTotalProjectAmount)
      setProjectAmountPaid(advancePaymentValue)
      setBalanceDue(tempTotalProjectAmount - advancePaymentValue)
      setBalDueAmountPaid(advancePaymentValue)

      setScheduledInvoice(response?.content?.scheduled)
      setProjectInvoice(response?.content?.project)
      if (response?.content?.scheduled) {
        const woResponse = await getWoList(
          response?.content?.work_order_list?.ids
        )
        const woNumbers = []
        woResponse.forEach(wo =>
          woNumbers.push({ id: wo.id, customer_po: wo.customer_po })
        )
        setMultipleWoNumbers(woNumbers)
      }
      setTimeout(() => {
        setData(response?.content)
        setLastSavedData(structuredClone(response?.content))
        setAuditTrail(response?.content?.history)
        setInvoiceStatus(response?.content?.status)
        setItemTypes(lineItemTypes)
        if (respRates?.company_rates) {
          setRates(respRates.company_rates)
        }
        if (respRates?.nte) {
          setPricingOverride({
            isPricingOverride: respRates.is_pricing_override_active,
            nte: respRates.nte
          })
        }
        if (respRates?.actual_duration) {
          if (respRates.actual_duration % 60 === 0) {
            setLaborQuantity(respRates.actual_duration / 60)
          } else {
            setLaborQuantity((respRates.actual_duration / 60).toFixed(1))
          }
        }
        setLoading(false)
        setDisableDraftButton(true)
      }, 2000)
    } catch (error) {
      /* eslint-disable no-console */
      console.error('An error ocurred when opening the invoice: ', error)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (
      !loading &&
      lineItems &&
      lineItems?.length > 0 &&
      !saveSubmitButtonsInitialized
    ) {
      const lineItemsTmp = [...lineItems].filter(
        item => !item.rate || !item.quantity || !item.amount
      )
      setDisableSubmitButton(lineItemsTmp?.length > 0)
      setSaveSubmitButtonsInitialized(true)
    }
  }, [loading])

  useEffect(() => {
    if (
      data?.status !== t('invoices.in_states.approved').toLowerCase() &&
      data?.status !== t('invoices.in_states.submitted').toLowerCase() &&
      data?.status !== t('invoices.in_states.paid').toLowerCase() &&
      data?.status !== t('invoices.in_states.hold').toLowerCase() &&
      data?.status !== t('invoices.in_states.voided').toLowerCase() &&
      !scheduledInvoice
    ) {
      setDisableSubmitButton(true)
    }
    setAuditTrail(data?.history)
    setInvoiceStatus(data?.status)

    // initialize line items
    if (!lineItems) setItems(data?.line_items)
    setNotes(data?.notes)
    const nteValue = data?.nte !== null ? Number(data?.nte) : 0
    setNteValue(nteValue)
    checkButtonsStatus()
  }, [data])

  useEffect(() => {
    let newLineItems = []
    lineItems?.forEach(item => {
      const currentRate = rates?.find(r =>
        r.type === 'Other' ? r.other_trade === item.item : r.type === item.item
      )
      item.editableQty = true
      if (item.item === t('invoices.items.labor')) {
        if (pricingOverride?.isPricingOverride) {
          item.editable = false
          item.editableQty = false
        } else {
          const matchingRate = rates?.find(
            r =>
              r.type === t('invoices.items.labor') &&
              r.trade?.toLowerCase() === item.trade?.toLowerCase()
          )
          if (matchingRate) {
            item.editable = false
          } else {
            item.editable = true
          }
        }
      } else {
        item.editable = !currentRate?.rate
      }
      newLineItems = [
        ...newLineItems,
        {
          id: item.id,
          item: item.item,
          description: item.description,
          trade: item.trade,
          rate: item.rate,
          quantity: item.quantity,
          amount: item.amount,
          rate_type: item.rate_type,
          editable: item.editable,
          editableQty: item.editableQty,
          product_name: item.product_name,
          product_description: item.product_description,
          task: item.task,
          vendor_edited: item.vendor_edited,
          subtotal_line_items: item.subtotal_line_items,
          sub_markdown: item.sub_markdown
        }
      ]
    })
    setAllItems(newLineItems)
    if (lineItems) {
      setBalanceDue(calculateTotal(lineItems) - amountPaid)
      setTotalProjectAmount(calculateTotal(lineItems))
    }
    checkButtonsStatus()
  }, [lineItems, totalAmount, pricingOverride, rates])

  useEffect(() => {
    checkButtonsStatus()
  }, [allItems])

  const isInvoiceNumberValid = () => {
    if (data?.internal_number === '') {
      const defaultNumber = 'WO#' + String(data?.work_order?.customer_po).trim()
      setData({ ...data, internal_number: defaultNumber })
    }
    if (isEqual(data?.internal_number, lastSavedData.internal_number)) {
      return true
    }
    return data?.internal_number?.length >= 3
  }
  // Saving draft
  const [editInvoiceButton, setEditInvoiceButton] = useState(false)
  const saveItems = async () => {
    setEditInvoiceButton(false)
    if (!isInvoiceNumberValid()) {
      dispatch(loadingActions.hide())
      return
    }

    setDisableDraftButton(true)
    setDisableSubmitButton(true)
    dispatch(loadingActions.show())
    const lineItemsTmp = [...lineItems].filter(item => item.amount !== 0)
    let total = 0
    lineItemsTmp?.forEach(item => {
      total += item.amount
    })
    const requestTmp = {
      line_items: lineItemsTmp,
      total: total,
      status: t('invoices.in_states.draft').toLowerCase(),
      notes: notes
    }

    if (data?.internal_number) {
      requestTmp.internal_number = data.internal_number
    }
    // call update function
    try {
      const response = await submitInvoice(data.id, { ...requestTmp }, false)
      setData(response?.content)
      setLastSavedData(structuredClone(response?.content))
      setAuditTrail([...response.content.history])
      setDisableDraftButton(true)
      setEditInvoiceButton(true)
      dispatch(loadingActions.hide())
      return response.content
    } catch (error) {
      setDisableSubmitButton(false)
      setEditInvoiceButton(false)
      dispatch(loadingActions.hide())
      /* eslint-disable no-console */
      console.error('An error ocurred when saving the draft: ', error)
    }
  }

  const checkButtonsStatus = () => {
    if (isDataReadyToSave() && isInvoiceNumberValid()) {
      if (
        totalAmount > 0 &&
        (nteValue === 0 || totalAmount <= nteValue || canExceedNTEConfig)
      ) {
        setDisableSubmitButton(false)
        setNteMessageEnabled(false)
        setDisableDraftButton(false)
      } else if (nteValue) {
        setDisableSubmitButton(nteValue < totalAmount)
        setNteMessageEnabled(nteValue < totalAmount)
        setDisableDraftButton(nteValue < totalAmount)
      } else if (!totalAmount) {
        setDisableSubmitButton(true)
        setDisableDraftButton(true)
      }
    } else {
      setDisableSubmitButton(true)
      setDisableDraftButton(true)
    }
  }

  // callback functions for InvoiceDetail components
  const setItems = items => {
    setLineItems(items)
    let total = 0
    items?.forEach(item => {
      total += item.amount
    })
    setTotalAmount(total)
  }

  // save draft on edit

  useEffect(() => {
    if (data?.notes?.trim() !== '') {
      setIsNotesFilled(true)
    } else {
      setIsNotesFilled(false)
    }
  }, [data?.notes])

  const handleChange = event => {
    setDisableDraftButton(
      event.target.value === lastSavedData.notes ||
        [...lineItems].filter(item => !item.rate || !item.quantity).length > 0
    )

    event.preventDefault()
    setNotes(event.target.value)
    data.notes = event.target.value
    setData(data)
    if (event.target.value.trim() !== '') {
      setIsNotesFilled(true)
    } else {
      setIsNotesFilled(false)
    }
  }

  const isValidItemSelected = () => {
    const arrayItems = [...(lineItems || [])]
    return arrayItems.every(({ item, quantity, amount, rate }) => {
      if (!quantity && !rate) return true
      if (item === '0' || amount === 0 || !amount) {
        return false
      }
      return item
    })
  }

  const validSelected = isValidItemSelected()

  useEffect(() => {
    setSelectedCheck(validSelected)
  }, [validSelected])

  const mandatoryNotesConfig = useSelector(
    state =>
      state.auth?.user?.userInfo?.configurations?.portals?.subcontractor
        ?.invoices?.mandatory_notes
  )

  const handleChatClick = () => {
    if (loadingFiles) {
      return
    }
    ReactGA.event({
      category: 'show',
      action: 'show_work_order_detail'
    })
    setSelectedWo({
      ...data?.work_order,
      status: 'completed',
      openedViaChat: true
    })
  }

  const isDataReadyToSave = () => {
    let result = false
    if (
      lineItems &&
      (!isEqual(data, lastSavedData) ||
        !isEqual(lineItems, lastSavedData?.line_items))
    ) {
      const lineItemsTmp = [...lineItems].filter(
        item =>
          !item.rate || !item.quantity || !item.amount || item.amount === 0
      )
      result = lineItemsTmp.length === 0
    }
    return result
  }

  const handleCloseWoPanel = () => {
    setSelectedWo(null)
  }

  const FormButtons = ({ draftButtonDisabled, submitButtonDisabled }) => {
    return (
      <Box className={classes.flexBox_end}>
        <Button
          variant="outlined"
          color="primary"
          className={classes.draftButton}
          onClick={async () => saveItems()}
          data-testid="save-invoice-button"
          disabled={draftButtonDisabled}
        >
          {t('proposals.actions.save')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={async () => {
            await handleSubmit({ ...data, notes: notes })
          }}
          data-testid="submit-invoice-button"
          disabled={submitButtonDisabled}
        >
          {t('proposals.actions.submit')}
        </Button>
      </Box>
    )
  }

  const handleAcceptSubmit = async value => {
    setDisableSubmitButton(true)
    setDisableDraftButton(true)
    if (value) {
      try {
        const newbill = await putCompanyBillDisclosure(userData.company_id)
        dispatch(
          authActions.setBillDisclosure({
            ...billDisclosureStore,
            ...newbill.bill_disclosure
          })
        )
      } catch (error) {
        dispatch(loadingActions.hide())
        console.error('Error saving bill disclosure: ', error)
      }
    }
    setSubmitConfirmation(state => ({ ...state, open: false }))
    await callInvoiceSubmit(data)
  }
  const handleCloseSubmitConfirmation = () => {
    setSubmitConfirmation(state => ({ ...state, open: false }))
  }

  const callInvoiceSubmit = async submitData => {
    const tempRequestBody = {
      ...data,
      status: t('invoices.in_states.submitted').toLowerCase(),
      work_order: submitData.work_order,
      due_date: submitData.due_date,
      term: submitData.term,
      history: [...submitData.history],
      internal_number: submitData.internal_number,
      notes: submitData.notes,
      attachments,
      line_items: allItems,
      total: totalAmount
    }
    try {
      setLoading(true)
      const response = await submitInvoice(submitData.id, tempRequestBody, true)
      setLastSavedData(structuredClone(response?.content))
      setData(response.content)
      setLoading(false)
      dispatch(loadingActions.hide())
    } catch (error) {
      dispatch(loadingActions.hide())
      console.error('Error submitting invoice: ', error)
    }
  }

  // submit button action
  const handleSubmit = async submitData => {
    if (!isInvoiceNumberValid()) {
      dispatch(loadingActions.hide())
      return
    }

    setDisableSubmitButton(true)
    dispatch(loadingActions.show())

    try {
      if (
        billDisclosureStore?.title &&
        billDisclosureStore?.content &&
        (!billDisclosureStore?.accepted || lienMandatory)
      ) {
        setSubmitConfirmation({ ...billDisclosureStore, open: true })
        dispatch(loadingActions.hide())
      } else if (
        mandatoryNotesConfig &&
        !data.notes?.trim() &&
        !projectInvoice
      ) {
        setConfirmDialogOpen(true)
        dispatch(loadingActions.hide())
      } else if (
        canExceedNTEConfig &&
        totalAmount > nteValue &&
        nteValue > 0 &&
        !projectInvoice
      ) {
        setNteExceedConfirmDialogOpen(true)
        dispatch(loadingActions.hide())
      } else {
        await callInvoiceSubmit(submitData)
      }
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Error submitting invoice: ', error)
    } finally {
      setDisableSubmitButton(false)
      dispatch(loadingActions.hide())
    }
  }
  // confirm dialog
  const handleConfirmAccept = () => {
    setConfirmDialogOpen(false)
    setNotes(dialogNotes)
    data.notes = dialogNotes
    setData(data)
    handleSubmit(null, { ...data, notes: dialogNotes })
  }
  const handleDialogNotesChange = e => {
    setDialogNotes(e.target.value)
  }

  // nte dialog
  const handleNteExceedConfirmAccept = async () => {
    dispatch(loadingActions.show())
    setDisableDraftButton(true)
    setDisableSubmitButton(true)
    setNteExceedConfirmDialogOpen(false)
    try {
      await callInvoiceSubmit(data)
    } catch (error) {
      console.error('Error submitting invoice: ', error)
    } finally {
      dispatch(loadingActions.hide())
      setDisableDraftButton(false)
      setDisableSubmitButton(false)
    }
  }

  // back button
  const handleLeavePage = () => {
    setOpenBackDialog(false)
    if (history.goBack.length === 0) {
      if (isRunningInIframe()) {
        history.push(
          '/invoices-iframe/' + params.id + '/' + params.access_token
        )
      } else {
        history.push({ pathname: 'invoices' })
      }
    } else {
      history.goBack()
    }
  }
  const handleCloseBackDialog = () => {
    setOpenBackDialog(false)
  }

  const UnsavedChangesDialog = ({ open, handleClose, handleLeave }) => {
    const classes = useStyles()

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent className={classes.dialogContainer}>
          <div className={classes.mainMessage}>
            {t('invoices.messages.leave_title')}
            <br />
            <br />
            <div className={classes.leaveTitle2}>
              {t('invoices.messages.leave_title2')}
            </div>
          </div>
          <br />
          <div className={classes.secondaryMessage}>
            {t('invoices.messages.leave_secondary')}
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActionsContainer}>
          <Button
            onClick={handleLeave}
            className={classes.leaveButton}
            variant="outlined"
          >
            {t('general.labels.leave_page2')}
          </Button>
          <Button
            onClick={handleClose}
            color="primary"
            className={classes.button}
            variant="contained"
          >
            {t('general.labels.continue2')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const handleCancelDialog = () => {
    setConfirmDialogOpen(false)
    setNteExceedConfirmDialogOpen(false)
    setDisableSubmitButton(false)
    setDisableDraftButton(false)
  }

  return (
    <Container className={classes.scrollContainer}>
      <ConfirmDialog
        open={confirmDialogOpen}
        style={{ height: '500px', width: '800px', overflowY: 'hidden' }}
        title={t('invoices.service_notes_title')}
        cancellabel={t('general.labels.cancel')}
        acceptlabel={t('general.labels.done')}
        handleCancel={handleCancelDialog}
        handleAccept={handleConfirmAccept}
        dialogNotes={dialogNotes}
        handleDialogNotesChange={handleDialogNotesChange}
        showInputBase={true}
      />
      <SubmitConfirmation
        open={submitConfirmation.open}
        setOpen={value =>
          setSubmitConfirmation(state => ({ ...state, open: value }))
        }
        title={submitConfirmation.title}
        message={submitConfirmation.content}
        handleAccept={handleAcceptSubmit}
        hideCheckBox={
          userStore?.userInfo?.configurations?.portals?.subcontractor?.invoices
            ?.lien_mandatory
        }
        handleClose={handleCloseSubmitConfirmation}
      />
      <ConfirmDialog
        open={nteExceedConfirmDialogOpen}
        title={t('invoices.nte_exceed_title')}
        cancellabel={t('general.labels.cancel')}
        acceptlabel={t('general.labels.submit')}
        handleCancel={handleCancelDialog}
        handleAccept={handleNteExceedConfirmAccept}
        message={t('invoices.nte_exceed_message')}
      />

      <UnsavedChangesDialog
        open={openBackDialog}
        handleClose={handleCloseBackDialog}
        handleLeave={handleLeavePage}
      />

      <DetailedInfo
        workOrder={selectedWo}
        handleClosePanel={handleCloseWoPanel}
        viewMode
        reload={reload}
        setReload={setReload}
      />
      <Container className={classes.container}>
        <Grid container spacing={2} className={classes.globalHeaderContainer}>
          <Grid item xs={2}>
            <Button
              disabled={loadingFiles}
              className={classes.backButton}
              variant="text"
              startIcon={<FontAwesomeIcon icon="fa-solid fa-angle-left" />}
              onClick={event => {
                if (loadingFiles) {
                  return
                }
                if (
                  !disableDraftButton &&
                  data?.status !==
                    t('invoices.in_states.approved').toLowerCase() &&
                  data?.status !==
                    t('invoices.in_states.submitted').toLowerCase() &&
                  data?.status !== t('invoices.in_states.paid').toLowerCase() &&
                  data?.status !== t('invoices.in_states.hold').toLowerCase() &&
                  data?.status !== t('invoices.in_states.voided').toLowerCase()
                ) {
                  setOpenBackDialog(true)
                } else {
                  handleLeavePage()
                }
              }}
            >
              {t('general.labels.back')}
            </Button>
          </Grid>
          <Grid item xs={8}></Grid>
          {paymentInquiryChat ? (
            <Grid item xs={2} className={classes.gridQuestion}>
              <Typography className={classes.question}>
                {t('invoices.related_questions')}
              </Typography>
              <Button
                disabled={loadingFiles}
                className={classes.chatButton}
                variant="text"
                startIcon={
                  <FontAwesomeIcon
                    icon="fa-solid fa-comments-question"
                    className={classes.chatIcon}
                  />
                }
                onClick={handleChatClick}
              >
                <Typography className={classes.chatText}>
                  {t('invoices.chat')}
                </Typography>
              </Button>
            </Grid>
          ) : (
            <Grid item xs={2} />
          )}
        </Grid>
        <Container className={classes.mainContainer}>
          <Container className={classes.leftContainer}>
            <Box className={classes.invoiceHeader} elevation={0}>
              <InvoiceHeaderComponent
                data={data}
                totalAmount={totalAmount}
                setData={setData}
                setLastSavedData={setLastSavedData}
                currentStatus={invoiceStatus}
                loading={loading}
                scheduled={scheduledInvoice}
                multipleWoNumbers={multipleWoNumbers}
                isValidItemSelected={validSelected}
                selectedCheck={selectedCheck}
                balanceDue={balanceDue}
                project={projectInvoice}
                disableSubmitButton={setDisableSubmitButton}
                disableDraftButton={setDisableDraftButton}
                attachments={attachments}
                setAttachments={setAttachments}
                nteMessageEnabled={nteMessageEnabled}
                items={lineItems}
                editInvoiceButton={editInvoiceButton}
                checkButtonsStatus={checkButtonsStatus}
              />
              <Box className={classes.invoiceDetail}>
                {data?.scheduled ? (
                  <ScheduledDetailComponent
                    itemTag={t('invoices.items.scheduled')}
                    items={data?.line_items}
                  />
                ) : data?.project ? (
                  <div>
                    {data?.work_order.advance_payment_percentage > 0 && (
                      <AdvancePaymentDetailComponent
                        itemTag={t('invoices.items.advance_payment')}
                        advancePaymentPercentage={
                          data?.work_order.advance_payment_percentage
                        }
                        woDateCreated={moment(
                          new Date(data?.date_created * 1000)
                        ).format(t('general.format.short_date'))}
                        projectAmountPaid={projectAmountPaid}
                        totalProjectAmount={totalProjectAmount}
                        status={data?.status}
                      />
                    )}
                    <McssfrScheduledDetailComponent
                      itemTag={t('invoices.items.scheduled')}
                      items={lineItems}
                      status={data?.status}
                      updateItems={setItems}
                      setDisableDraftButton={setDisableDraftButton}
                      setDisableSubmitButton={setDisableSubmitButton}
                    />
                  </div>
                ) : (
                  <>
                    <InvoiceDetailv2Component
                      itemTag={t('invoices.items.all')}
                      tradeTag={data?.work_order?.category}
                      addItemTag={t('invoices.in_actions.add_item')}
                      updateItems={setItems}
                      items={lineItems}
                      itemTypes={itemTypes}
                      rates={rates}
                      pricingEngine={pricingEngine}
                      status={data?.status}
                      loading={loading}
                      scheduled={scheduledInvoice}
                      workOrderDate={data?.work_order?.date_dispatched}
                      isPricingOverride={pricingOverride.isPricingOverride}
                      nte={pricingOverride.nte}
                      laborQuantity={laborQuantity}
                      setDisableDraftButton={setDisableDraftButton}
                    />
                  </>
                )}
                <Box className={isFAS ? classes.notesContainerHidden : ''}>
                  <Box
                    className={
                      (userStore.userInfo.parent_subdomain === mcssfr ||
                        userStore.userInfo.parent_subdomain === mcs360) &&
                      data?.project
                        ? classes.notesContainerHidden
                        : classes.notesContainer
                    }
                  >
                    <Box
                      colSpan={8}
                      classes={{
                        root:
                          (mandatoryNotesConfig && !data?.notes) ||
                          !isNotesFilled
                            ? classes.notesLabelMandatory
                            : classes.notesLabel
                      }}
                    >
                      {loading
                        ? ''
                        : (mandatoryNotesConfig ? '* ' : '') +
                          (notesLabelConfig || t('invoices.notes'))}
                    </Box>
                    {loading ? (
                      <LoadingBox width="50%" height="50px" />
                    ) : (
                      <InputBase
                        id="misc-input"
                        placeholder={t('invoices.notes_placeholder')}
                        multiline
                        rows={4}
                        classes={{
                          root:
                            mandatoryNotesConfig && !data?.notes
                              ? classes.miscNotesMandatory
                              : classes.miscNotes
                        }}
                        value={notes}
                        onChange={event => handleChange(event)}
                        disabled={
                          data?.status ===
                            t('invoices.in_states.approved').toLowerCase() ||
                          data?.status ===
                            t('invoices.in_states.submitted').toLowerCase() ||
                          data?.status ===
                            t('invoices.in_states.paid').toLowerCase() ||
                          data?.status ===
                            t('invoices.in_states.hold').toLowerCase() ||
                          data?.status ===
                            t('invoices.in_states.voided').toLowerCase() ||
                          scheduledInvoice
                        }
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
          <Container className={classes.rightContainer}>
            <InvoiceTimeline steps={auditTrail} loading={loading} />
          </Container>
          {editableInvoiceStatus.includes(data?.status) && (
            <Container className={classes.bottomContainer}>
              <FormButtons
                draftButtonDisabled={disableDraftButton}
                submitButtonDisabled={disableSubmitButton}
              />
            </Container>
          )}
        </Container>
      </Container>
    </Container>
  )
}
export default CreateInvoice
